import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { Data } from '@puckeditor/core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { fetchTemplateBundle } from '../../features/editor/api';
import type { TemplateCatalog } from '../../types/template';
import { TemplatePreviewPane } from '../sites/TemplatePreviewPane';

export type TemplatePreviewDialogProps = {
  open: boolean;
  template: TemplateCatalog | null;
  onOpenChange: (open: boolean) => void;
  onSelect: (template: TemplateCatalog) => void;
};

export const TemplatePreviewDialog = ({
  open,
  template,
  onOpenChange,
  onSelect,
}: TemplatePreviewDialogProps) => {
  const [data, setData] = useState<Data | null>(null);
  const [themeCss, setThemeCss] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !template) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const bundle = await fetchTemplateBundle(template.id);
        if (cancelled) return;
        setData(bundle.data as Data);
        setThemeCss(bundle.themeCss ?? '');
        setCustomCss(bundle.customCss ?? '');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load template preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, template]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[85vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-gutter pt-5 pb-3">
          <DialogTitle>{template?.displayName ?? 'Template preview'}</DialogTitle>
          {template?.description && (
            <DialogDescription>{template.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          {error ? (
            <div className="p-gutter text-sm text-destructive">{error}</div>
          ) : (
            <TemplatePreviewPane
              templateName={template?.displayName ?? null}
              data={data}
              themeCss={themeCss}
              customCss={customCss}
              loading={loading}
              canCustomize={false}
              onCustomize={() => undefined}
              hideHeader
            />
          )}
        </div>

        <DialogFooter className="px-gutter py-4">
          <Button
            type="button"
            variant="tertiary"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => template && onSelect(template)}
            disabled={!template || loading}
          >
            Select
            <ArrowRight className="h-4 w-4 ml-1.5 stroke-[1.5]" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePreviewDialog;
